// src/pages/AdminLeavesPage.js
import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminLeavesPage.css";
import NoirAlert from "../components/NoirAlert";
import RejectLeaveDialog from "../components/RejectLeaveDialog";
import PaginationControls from "../components/PaginationControls";
import LeaveDetailsModal from "../components/LeaveDetailsModal";
import { useI18n } from "../providers/I18nProvider";
import { apiClient } from "../services/apiClient";
import { useConfirm } from "../providers/ConfirmProvider";

const normalizeRoleFromId = (roleId) => {
  const id = Number(roleId);
  if (id === 1) return "company_manager";
  if (id === 3) return "unit_supervisor";
  if (id === 4) return "hr";
  if (id === 6) return "admin";
  return "";
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const EDITABLE_APPROVAL_WINDOW_DAYS = 15;

const toDateKey = (value) => {
  if (!value) return "";
  const s = String(value);
  if (/^\\d{4}-\\d{2}-\\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const isPendingApprovalEditableLeave = (leave) => {
  if (!leave) return false;
  if (normalizeStatus(leave.status) !== "beklemede") return false;
  if (normalizeStatus(leave.current_stage) === "final") return false;

  const startKey = toDateKey(leave.start_date);
  if (!startKey) return false;
  const createdAt = leave.created_at ? new Date(leave.created_at) : null;
  if (!createdAt || Number.isNaN(createdAt.getTime())) return false;

  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const startDateObj = new Date(`${startKey}T00:00:00Z`);
  if (startDateObj < today) return false;

  const windowMs = EDITABLE_APPROVAL_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  if (Date.now() - createdAt.getTime() > windowMs) return false;

  return true;
};

const AdminLeavesPage = () => {
  const confirm = useConfirm();
  const { t } = useI18n();

  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [roleName, setRoleName] = useState(null);
  const [myId, setMyId] = useState(null);

  const [editLeave, setEditLeave] = useState(null);
  const [editForm, setEditForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    half_day_period: "",
    reason: "",
  });

  const [detailLeaveId, setDetailLeaveId] = useState(null);
  const [detailInitial, setDetailInitial] = useState(null);

  // HR iptali için dialog state'i
  const [hrCancelLeave, setHrCancelLeave] = useState(null);
  const [hrCancelRestore, setHrCancelRestore] = useState(true);
  const [hrCancelReason, setHrCancelReason] = useState("");

  const pageSizeKey = "pageSize.adminLeaves";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  // Liste organizasyonu: sekme + arama + filtreler
  const [tab, setTab] = useState("pending"); // mine | pending | approved | closed | all
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");
  // NOTE: don't rely on backend always returning normalized roles in older DBs
  const normalizeRoleName = (value, fallbackRoleId = null) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return normalizeRoleFromId(fallbackRoleId);
    if (raw === "manager") return "unit_supervisor";
    return raw;
  };

  const normalizeStage = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    return raw === "manager" ? "unit_supervisor" : raw;
  };

  const prettyStatus = (value) => {
    if (!value) return "—";
    const lower = String(value).toLowerCase();
    if (lower === "beklemede") return t("leave.status.pending");
    if (lower === "onaylandı") return t("leave.status.approved");
    if (lower === "reddedildi") return t("leave.status.rejected");
    if (lower === "atlandı") return t("leave.status.skipped");
    if (lower === "i̇ptal edildi" || lower === "iptal edildi") return t("leave.status.cancelled");
    return value;
  };

  const prettyStageName = (stage) => {
    const normalized = normalizeStage(stage);
    const map = {
      unit_supervisor: t("leave.stage.unitSupervisor"),
      company_manager: t("leave.stage.companyManager"),
      hr: t("leave.stage.hr"),
    };
    return map[normalized] || normalized || "—";
  };

  const isPending = (status) => normalizeStatus(status) === "beklemede";
  const isApproved = (status) => normalizeStatus(status) === "onaylandı";

  const normalizedRole = useMemo(
    () => normalizeRoleName(roleName, null) || "",
    [roleName]
  );
  const approvalRole = normalizedRole === "admin" ? "company_manager" : normalizedRole;

  const editSelectedType = useMemo(() => {
    const id = Number(editForm.leave_type_id);
    if (!Number.isFinite(id)) return null;
    return (leaveTypes || []).find((lt) => Number(lt.id) === id) || null;
  }, [leaveTypes, editForm.leave_type_id]);

  const editIsHourly = Boolean(editSelectedType?.is_duration_flexible);
  const editIsAnnual = String(editSelectedType?.name || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .startsWith("yıllık izin");
  const editHalfDayPeriod = String(editForm.half_day_period || "").trim().toUpperCase();
  const editIsHalfDay = !editIsHourly && editIsAnnual && (editHalfDayPeriod === "AM" || editHalfDayPeriod === "PM");

  const canActOnLeave = (leave) => {
    if (!leave || !isPending(leave.status)) return false;
    const stage = normalizeStage(leave.current_stage);
    const role = approvalRole;

    if (!stage || !role) return false;
    if (role === "company_manager") {
      if (stage === "company_manager") return true;
      // Birim amiri aşamasını, atanmış amir/ekip yöneticisi olan company_manager
      // da onaylayabilir (backend ile tutarlı).
      if (stage === "unit_supervisor") {
        if (myId == null) return false;
        return (
          (leave.manager_id != null && Number(leave.manager_id) === Number(myId)) ||
          (leave.team_manager_id != null && Number(leave.team_manager_id) === Number(myId))
        );
      }
      return false;
    }
    if (role === "hr") return stage === "hr";
    if (role === "unit_supervisor") {
      if (stage !== "unit_supervisor") return false;
      return true; // backend enforces ownership/assignment
    }
    return false;
  };

  const openEdit = (leave) => {
    setEditLeave(leave);
    setEditForm({
      leave_type_id: leave?.leave_type_id ? String(leave.leave_type_id) : "",
      start_date: toDateKey(leave?.start_date),
      end_date: toDateKey(leave?.end_date),
      start_time: leave?.start_time ? String(leave.start_time).slice(0, 5) : "",
      end_time: leave?.end_time ? String(leave.end_time).slice(0, 5) : "",
      half_day_period: leave?.half_day_period ? String(leave.half_day_period) : "",
      reason: leave?.reason || "",
    });
  };

  const closeEdit = () => {
    setEditLeave(null);
    setEditForm({
      leave_type_id: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      half_day_period: "",
      reason: "",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editLeave) return;

    const leaveTypeId = Number(editForm.leave_type_id);
    if (!Number.isFinite(leaveTypeId) || !editForm.start_date || !editForm.reason?.trim()) {
      setNotification({ message: t("leave.edit.required"), type: "error" });
      return;
    }

    const selectedType = (leaveTypes || []).find((lt) => Number(lt.id) === leaveTypeId);
    const isHourly = Boolean(selectedType?.is_duration_flexible);
    const isAnnual = String(selectedType?.name || "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .startsWith("yıllık izin");
    const halfDayPeriod = String(editForm.half_day_period || "").trim().toUpperCase();
    const isHalfDay = !isHourly && isAnnual && (halfDayPeriod === "AM" || halfDayPeriod === "PM");

    if (isHourly) {
      if (!editForm.start_time || !editForm.end_time) {
        setNotification({ message: t("leave.edit.timeRequired"), type: "error" });
        return;
      }
    } else if (!isHalfDay && !editForm.end_date) {
      setNotification({ message: t("leave.edit.endDateRequired"), type: "error" });
      return;
    }

    const payload = {
      leave_type_id: leaveTypeId,
      start_date: editForm.start_date,
      end_date: isHourly || isHalfDay ? editForm.start_date : editForm.end_date,
      reason: editForm.reason,
      half_day_period: isHalfDay ? halfDayPeriod : null,
      ...(isHourly ? { start_time: editForm.start_time, end_time: editForm.end_time } : {}),
    };

    try {
      const url =
        normalizedRole === "hr" ? `/leaves/${editLeave.id}/pending-edit` : `/leaves/${editLeave.id}`;

      const response =
        normalizedRole === "hr"
          ? await apiClient.patch(url, payload)
          : await apiClient.put(url, payload);

      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === editLeave.id ? { ...l, ...updated } : l)));
      closeEdit();
      setNotification({
        message: normalizedRole === "hr" ? t("admin.leaves.editRequested") : t("leave.edit.updateSuccess"),
        type: "success",
      });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const approvePendingEdit = async (leaveId) => {
    try {
      const response = await apiClient.patch(`/leaves/${leaveId}/pending-edit/approve`, {});
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === leaveId ? { ...l, ...updated } : l)));
      setNotification({ message: t("admin.leaves.editApproved"), type: "success" });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const rejectPendingEdit = async (leaveId) => {
    try {
      const response = await apiClient.patch(`/leaves/${leaveId}/pending-edit/reject`, {});
      const updated = response.data;
      setLeaves((prev) => prev.map((l) => (l.id === leaveId ? { ...l, ...updated } : l)));
      setNotification({ message: t("admin.leaves.editRejected"), type: "success" });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  useEffect(() => {
    apiClient
      .get("/users/me")
      .then((res) => {
        const resolvedRole =
          normalizeRoleName(res.data?.role_name, res.data?.role_id) ||
          normalizeRoleFromId(res.data?.role_id) ||
          "employee";
        setRoleName(resolvedRole);
        setMyId(res.data?.id ?? null);
      })
      .catch(() => {
        // ignore
      });
  }, []);

  useEffect(() => {
    const fetchLeaves = async () => {
      try {
        const [leavesRes, typesRes] = await Promise.all([
          apiClient.get("/leaves/admin"),
          apiClient.get("/leave-types"),
        ]);
        setLeaves(leavesRes.data);
        setLeaveTypes(typesRes.data || []);
      } catch (err) {
        console.error("İzinleri çekerken hata:", err);
        //alert( "İzinler yüklenirken bir hata oluştu: " + (err.response?.data?.msg || err.message));
        setNotification({
          message:
            t("admin.leaves.loadErrorPrefix") +
            (err.response?.data?.msg || err.message),
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchLeaves();
  }, [t]);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const busyIdsRef = React.useRef(new Set());
  const updateLeaveStatus = async (id, status, reason = null) => {
    if (busyIdsRef.current.has(id)) return; // çift tıklama koruması
    busyIdsRef.current.add(id);
    try {
      const response = await apiClient.patch(`/leaves/${id}/status`, { status, reason });

      const updated = response.data;
      setLeaves((prev) =>
        prev.map((leave) =>
          leave.id === id ? { ...leave, ...updated } : leave
        )
      );

      const warningMsg =
        Array.isArray(updated?.warnings) && updated.warnings[0]?.message
          ? updated.warnings[0].message
          : "";
      if (warningMsg) {
        setNotification({ message: warningMsg, type: "warning" });
      } else {
        setNotification({ message: t("common.saved"), type: "success" });
      }
    } catch (err) {
      console.error("İzin durumu güncellenirken hata:", err);
      let details =
        err?.response?.data?.error ||
        err?.response?.data?.msg ||
        err?.message ||
        t("common.genericError");

      // Some endpoints may return a Blob on error
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          try {
            const parsed = JSON.parse(text);
            details = parsed.msg || parsed.error || text;
          } catch (_) {
            details = text;
          }
        }
      } catch (_) {
        // ignore
      }

      setNotification({
        message: t("admin.leaves.updateErrorPrefix") + details,
        type: "error",
      });
    } finally {
      busyIdsRef.current.delete(id);
    }
  };

  const handleApprove = (id) => updateLeaveStatus(id, "Onaylandı");
  const [rejectLeave, setRejectLeave] = useState(null);
  const handleReject = (id) => {
    setRejectLeave(leaves.find((l) => l.id === id) || null);
  };

  const handleDelete = async (leave) => {
    if (!leave?.id) return;
    const ok = await confirm(t("leave.cancelConfirm"));
    if (!ok) return;
    try {
      await apiClient.delete(`/leaves/${leave.id}`);
      setLeaves((prev) => prev.filter((item) => item.id !== leave.id));
      setNotification({ message: t("leave.cancelSuccess"), type: "success" });
    } catch (err) {
      setNotification({
        message: err?.response?.data?.msg || err?.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const openHrCancel = (leave) => {
    setHrCancelLeave(leave);
    setHrCancelRestore(true);
    setHrCancelReason("");
  };
  const closeHrCancel = () => {
    setHrCancelLeave(null);
    setHrCancelRestore(true);
    setHrCancelReason("");
  };
  const confirmHrCancel = async () => {
    if (!hrCancelLeave?.id) return;
    try {
      const response = await apiClient.post(
        `/leaves/${hrCancelLeave.id}/hr-cancel`,
        {
          restore_balance: hrCancelRestore,
          reason: hrCancelReason?.trim() || null,
        }
      );
      const updated = response.data;
      setLeaves((prev) =>
        prev.map((l) => (l.id === hrCancelLeave.id ? { ...l, ...updated } : l))
      );
      setNotification({
        message: hrCancelRestore
          ? t("admin.leaves.hrCancelSuccessRestored")
          : t("admin.leaves.hrCancelSuccess"),
        type: "success",
      });
      closeHrCancel();
    } catch (err) {
      setNotification({
        message:
          err?.response?.data?.msg ||
          err?.message ||
          t("common.genericError"),
        type: "error",
      });
    }
  };

  const formatTime = (str) => (str ? str.slice(0, 5) : "");

  const downloadPdf = async (id) => {
    try {
      const response = await apiClient.get(`/leaves/${id}/document`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `leave-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      try {
        const blob = err.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          try {
            const parsed = JSON.parse(text);
            setNotification({
              message: parsed.msg || parsed.error || text,
              type: "error",
            });
            return;
          } catch (_) {
            setNotification({ message: text, type: "error" });
            return;
          }
        }
      } catch (_) {
        // ignore
      }
      setNotification({
        message: err.response?.data?.msg || err.message || t("common.genericError"),
        type: "error",
      });
    }
  };

  const isClosed = (status) => {
    const st = normalizeStatus(status);
    return st === "reddedildi" || st === "iptal edildi" || st === "i̇ptal edildi";
  };

  const tabCounts = useMemo(() => {
    const counts = { mine: 0, pending: 0, approved: 0, closed: 0, all: leaves.length };
    leaves.forEach((l) => {
      if (isPending(l.status)) {
        counts.pending += 1;
        if (canActOnLeave(l)) counts.mine += 1;
      } else if (isApproved(l.status)) counts.approved += 1;
      else if (isClosed(l.status)) counts.closed += 1;
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves, approvalRole, myId]);

  const filteredLeaves = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const list = leaves.filter((l) => {
      if (tab === "mine" && !(isPending(l.status) && canActOnLeave(l))) return false;
      if (tab === "pending" && !isPending(l.status)) return false;
      if (tab === "approved" && !isApproved(l.status)) return false;
      if (tab === "closed" && !isClosed(l.status)) return false;
      if (typeFilter && String(l.leave_type_id) !== typeFilter) return false;
      // Tarih aralığı kesişimi: [start,end] ile [from,to]
      const startKey = toDateKey(l.start_date);
      const endKey = toDateKey(l.end_date) || startKey;
      if (fromDate && endKey && endKey < fromDate) return false;
      if (toDate && startKey && startKey > toDate) return false;
      if (q) {
        const hay = `${l.user_name || ""} ${l.user_surname || ""} ${l.request_code || ""} ${
          l.leave_type_name || ""
        }`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const ts = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const sorters = {
      created_desc: (a, b) => ts(b.created_at) - ts(a.created_at),
      created_asc: (a, b) => ts(a.created_at) - ts(b.created_at),
      start_asc: (a, b) => ts(a.start_date) - ts(b.start_date),
      start_desc: (a, b) => ts(b.start_date) - ts(a.start_date),
    };
    return [...list].sort(sorters[sortKey] || sorters.created_desc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaves, tab, search, typeFilter, fromDate, toDate, sortKey, approvalRole, myId]);

  useEffect(() => {
    setPage(1);
  }, [tab, search, typeFilter, fromDate, toDate, sortKey]);

  // Sayfa sınırı ekrandaki (filtrelenmiş) listeye göre: onaylanan son satır sekmeden
  // düşünce boş sayfada kalmasın.
  useEffect(() => {
    const totalPages = Math.ceil((filteredLeaves.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredLeaves.length, page, pageSize]);

  const pagedLeaves = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLeaves.slice(start, start + pageSize);
  }, [filteredLeaves, page, pageSize]);

  const hasActiveFilter = Boolean(search || typeFilter || fromDate || toDate);
  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setFromDate("");
    setToDate("");
  };

  const waitingDays = (leave) => {
    const d = new Date(leave.created_at);
    if (Number.isNaN(d.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  };

  const TABS = [
    { key: "mine", label: t("admin.leaves.tabs.mine") },
    { key: "pending", label: t("admin.leaves.tabs.pending") },
    { key: "approved", label: t("admin.leaves.tabs.approved") },
    { key: "closed", label: t("admin.leaves.tabs.closed") },
    { key: "all", label: t("admin.leaves.tabs.all") },
  ];

  return (
    <div className="admin-leaves-page">
      <h2 className="admin-title">{t("admin.leaves.title")}</h2>
      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />

      {loading ? (
        <p className="muted">{t("common.loading")}</p>
      ) : leaves.length === 0 ? (
        <p className="muted">{t("admin.leaves.empty")}</p>
      ) : (
        <div className="admin-leaves-container">
          <div className="leaves-tabs" role="tablist">
            {TABS.map((tb) => (
              <button
                key={tb.key}
                type="button"
                role="tab"
                aria-selected={tab === tb.key}
                className={`leaves-tab${tab === tb.key ? " active" : ""}${
                  tb.key === "mine" && tabCounts.mine > 0 ? " attention" : ""
                }`}
                onClick={() => setTab(tb.key)}
              >
                {tb.label}
                <span className="leaves-tab-count">{tabCounts[tb.key]}</span>
              </button>
            ))}
          </div>
          <div className="leaves-toolbar">
            <input
              type="search"
              className="leaves-filter-input"
              placeholder={t("admin.leaves.filters.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="leaves-filter-input"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              aria-label={t("admin.leaves.columns.type")}
            >
              <option value="">{t("admin.leaves.filters.allTypes")}</option>
              {(leaveTypes || []).map((lt) => (
                <option key={lt.id} value={String(lt.id)}>
                  {lt.name}
                </option>
              ))}
            </select>
            <label className="leaves-filter-date">
              <span>{t("admin.leaves.filters.from")}</span>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="leaves-filter-date">
              <span>{t("admin.leaves.filters.to")}</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <select
              className="leaves-filter-input"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("admin.leaves.filters.sort")}
            >
              <option value="created_desc">{t("admin.leaves.filters.sortCreatedDesc")}</option>
              <option value="created_asc">{t("admin.leaves.filters.sortCreatedAsc")}</option>
              <option value="start_asc">{t("admin.leaves.filters.sortStartAsc")}</option>
              <option value="start_desc">{t("admin.leaves.filters.sortStartDesc")}</option>
            </select>
            {hasActiveFilter && (
              <button type="button" className="btn" onClick={resetFilters}>
                {t("admin.leaves.filters.reset")}
              </button>
            )}
          </div>
          {filteredLeaves.length === 0 && (
            <p className="muted leaves-empty">{t("admin.leaves.emptyFiltered")}</p>
          )}
          <PaginationControls
            totalItems={filteredLeaves.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          <div className="table-wrap">
            <table className="admin-leaves-table">
              <thead>
                <tr>
                  <th>{t("admin.leaves.code")}</th>
                  <th>{t("admin.leaves.columns.requester")}</th>
                  <th>{t("admin.leaves.columns.type")}</th>
                  <th>{t("admin.leaves.createdAt")}</th>
                  <th>{t("admin.leaves.start")}</th>
                  <th>{t("admin.leaves.end")}</th>
                  <th>{t("admin.leaves.columns.duration")}</th>
                  <th>{t("admin.leaves.balanceBefore")}</th>
                  <th>{t("admin.leaves.balanceAfter")}</th>
                  <th>{t("leave.overallStatus")}</th>
                  <th>{t("leave.nextStage")}</th>
                  <th>{t("admin.leaves.approvalChain")}</th>
                  <th>{t("admin.leaves.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {pagedLeaves.map((leave) => {
                  const durationText =
                    leave.is_duration_flexible ||
                    leave.calculated_days == null ||
                    Number(leave.calculated_days) === 0
                      ? leave.start_time && leave.end_time
                        ? `${formatTime(leave.start_time)} - ${formatTime(leave.end_time)}`
                        : t("admin.leaves.missingInfo")
                      : `${leave.calculated_days} ${t("leave.summary.days")}`;

                  const isHourly =
                    leave.is_duration_flexible ||
                    leave.total_duration_hours != null;
                  const balBefore = isHourly
                    ? leave.balance_before_hours
                    : leave.balance_before_days;
                  const balAfter = isHourly
                    ? leave.balance_after_hours
                    : leave.balance_after_days;
                  const balUnit = isHourly ? "saat" : "gün";
                  const formatBal = (v) =>
                    v === null || v === undefined || v === ""
                      ? "—"
                      : `${Number(v)} ${balUnit}`;
                  return (
                    <tr key={leave.id}>
                      <td className="mono">{leave.request_code || `#${leave.id}`}</td>
                      <td className="cell-strong">
                        {leave.user_name} {leave.user_surname}
                      </td>
                      <td>
                        {leave.leave_type_name}
                        {leave.leave_sub_type_name ? ` (${leave.leave_sub_type_name})` : ""}
                      </td>
                      <td className="mono">{leave.created_at ? String(leave.created_at).slice(0, 10) : "—"}</td>
                      <td className="mono">{String(leave.start_date).slice(0, 10)}</td>
                      <td className="mono">{String(leave.end_date).slice(0, 10)}</td>
                      <td>{durationText}</td>
                      <td className="mono">{formatBal(balBefore)}</td>
                      <td className="mono">{formatBal(balAfter)}</td>
                      <td>
                        <span
                          className={`status-pill status-${String(leave.status || "").toLowerCase()}`}
                        >
                          {prettyStatus(leave.status)}
                        </span>
                      </td>
                      <td>
                        {isPending(leave.status) && leave.current_stage ? (
                          <>
                            {prettyStageName(leave.current_stage)}
                            {waitingDays(leave) != null && (
                              <div className={`waiting-days${waitingDays(leave) >= 3 ? " late" : ""}`}>
                                {waitingDays(leave)} {t("admin.leaves.waitingDays")}
                              </div>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div className="approval-chain">
                          {[
                            [t("leave.stage.unitSupervisor"), leave.manager_status],
                            [t("leave.stage.companyManager"), leave.company_status],
                            [t("leave.stage.hr"), leave.hr_status],
                          ].map(([label, st]) => (
                            <span
                              key={label}
                              className={`chain-chip chain-${normalizeStatus(st) || "none"}`}
                              title={`${label}: ${prettyStatus(st)}`}
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="actions">
                          <button
                            className="btn"
                            type="button"
                            onClick={() => {
                              setDetailInitial(leave);
                              setDetailLeaveId(leave.id);
                            }}
                          >
                            {t("admin.leaves.details")}
                          </button>
                          {isApproved(leave.status) && (
                            <button className="btn btn-pdf" type="button" onClick={() => downloadPdf(leave.id)}>
                              {t("leave.document.downloadShort")}
                            </button>
                          )}
                          {canActOnLeave(leave) ? (
                            <>
                              <button className="btn btn-approve" type="button" onClick={() => handleApprove(leave.id)}>
                                {t("admin.leaves.approve")}
                              </button>
                              <button className="btn btn-reject" type="button" onClick={() => handleReject(leave.id)}>
                                {t("admin.leaves.reject")}
                              </button>
                            </>
                          ) : !isApproved(leave.status) ? (
                            <span className="muted">{t("admin.leaves.noAction")}</span>
                          ) : null}

                          {isPendingApprovalEditableLeave(leave) && ["company_manager", "admin"].includes(normalizedRole) && (
                            <button className="btn btn-edit" type="button" onClick={() => openEdit(leave)}>
                              {t("common.edit")}
                            </button>
                          )}
                          {isPendingApprovalEditableLeave(leave) && normalizedRole === "hr" && (
                            <button className="btn btn-edit" type="button" onClick={() => openEdit(leave)}>
                              {t("admin.leaves.requestEdit")}
                            </button>
                          )}

                          {["company_manager", "admin"].includes(normalizedRole) && leave.pending_edit_payload && (
                            <>
                              <button
                                className="btn btn-approve"
                                type="button"
                                onClick={() => approvePendingEdit(leave.id)}
                              >
                                {t("admin.leaves.approveEdit")}
                              </button>
                              <button
                                className="btn btn-reject"
                                type="button"
                                onClick={() => rejectPendingEdit(leave.id)}
                              >
                                {t("admin.leaves.rejectEdit")}
                              </button>
                            </>
                          )}
                          {normalizedRole === "admin" && (
                            <button className="btn btn-reject" type="button" onClick={() => handleDelete(leave)}>
                              {t("common.delete")}
                            </button>
                          )}
                          {normalizedRole === "hr" && isApproved(leave.status) && (
                            <button
                              className="btn btn-reject"
                              type="button"
                              onClick={() => openHrCancel(leave)}
                            >
                              {t("admin.leaves.hrCancel")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls
            totalItems={filteredLeaves.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <RejectLeaveDialog
        leave={rejectLeave}
        onConfirm={(id, reason) => updateLeaveStatus(id, "Reddedildi", reason)}
        onClose={() => setRejectLeave(null)}
      />

      {detailLeaveId && (
        <LeaveDetailsModal
          leaveId={detailLeaveId}
          initial={detailInitial}
          onClose={() => {
            setDetailLeaveId(null);
            setDetailInitial(null);
          }}
        />
      )}

      {hrCancelLeave && (
        <div className="modal-backdrop" onMouseDown={closeHrCancel}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>{t("admin.leaves.hrCancelTitle")}</h3>
            <p>
              {t("admin.leaves.hrCancelIntro", {
                person:
                  `${hrCancelLeave.user_name || ""} ${hrCancelLeave.user_surname || ""}`.trim(),
                type: hrCancelLeave.leave_type_name || "",
                start: String(hrCancelLeave.start_date || "").slice(0, 10),
                end: String(hrCancelLeave.end_date || "").slice(0, 10),
              })}
            </p>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={hrCancelRestore}
                onChange={(e) => setHrCancelRestore(e.target.checked)}
              />
              <span>{t("admin.leaves.hrCancelRestoreBalance")}</span>
            </label>
            <label style={{ display: "block", marginTop: 10 }}>
              {t("admin.leaves.hrCancelReasonLabel")}
              <textarea
                rows={3}
                value={hrCancelReason}
                onChange={(e) => setHrCancelReason(e.target.value)}
                placeholder={t("admin.leaves.hrCancelReasonPlaceholder")}
                style={{ width: "100%", marginTop: 4 }}
              />
            </label>
            <div className="form-actions" style={{ marginTop: 12 }}>
              <button className="ghost" type="button" onClick={closeHrCancel}>
                {t("common.cancel")}
              </button>
              <button
                className="btn btn-reject"
                type="button"
                onClick={confirmHrCancel}
              >
                {t("admin.leaves.hrCancelConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {editLeave && (
        <div className="modal-backdrop" onMouseDown={closeEdit}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>
              {normalizedRole === "hr"
                ? t("admin.leaves.requestEdit")
                : t("leave.edit.modalTitle")}
            </h3>
            <form className="form-grid" onSubmit={submitEdit}>
              <label>
                {t("leave.form.leaveType")}
                <select
                  value={editForm.leave_type_id}
                  onChange={(e) => setEditForm((p) => ({ ...p, leave_type_id: e.target.value }))}
                >
                  <option value="">{t("leave.form.leaveTypeSelect")}</option>
                  {leaveTypes.map((lt) => (
                    <option key={lt.id} value={lt.id}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t("leave.form.startDate")}
                <input
                  type="date"
                  value={editForm.start_date}
                  onChange={(e) => setEditForm((p) => ({ ...p, start_date: e.target.value }))}
                />
              </label>
              {editIsAnnual && !editIsHourly && (
                <label>
                  Yarım gün
                  <select
                    value={editHalfDayPeriod}
                    onChange={(e) => setEditForm((p) => ({ ...p, half_day_period: e.target.value }))}
                  >
                    <option value="">Tam gün</option>
                    <option value="AM">Öğleden önce</option>
                    <option value="PM">Öğleden sonra</option>
                  </select>
                </label>
              )}
              {!editIsHourly && !editIsHalfDay && (
                <label>
                  {t("leave.form.endDate")}
                  <input
                    type="date"
                    value={editForm.end_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, end_date: e.target.value }))}
                  />
                </label>
              )}
              {editIsHourly && (
                <label>
                  {t("leave.form.startTime")}
                  <input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => setEditForm((p) => ({ ...p, start_time: e.target.value }))}
                  />
                </label>
              )}
              {editIsHourly && (
                <label>
                  {t("leave.form.endTime")}
                  <input
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => setEditForm((p) => ({ ...p, end_time: e.target.value }))}
                  />
                </label>
              )}
              <label className="span-2">
                {t("leave.form.reason")}
                <textarea
                  rows={3}
                  value={editForm.reason}
                  onChange={(e) => setEditForm((p) => ({ ...p, reason: e.target.value }))}
                />
              </label>
              <div className="form-actions">
                <button className="ghost" type="button" onClick={closeEdit}>
                  {t("common.cancel")}
                </button>
                <button className="btn btn-approve" type="submit">
                  {normalizedRole === "hr"
                    ? t("admin.leaves.sendEditRequest")
                    : t("leave.actions.update")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeavesPage;
